import { fetchAllPages } from '@frontend/api-utils';
import { IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { ProductClient, ProductKitClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { toError } from '../../../utils/to-error';

export interface KitReturnEntry {
    product: IProduct;
    quantity: number;
}

export interface UseKitForProductResult {
    kitId: string | null;
    entries: KitReturnEntry[];
    /**
     * The initial scan seed — contains the provided `seed` if its product
     * matches a kit entry, otherwise an empty array. A new array reference
     * is produced on each successful load so consumers can `useEffect` on it
     * to reset their scan state.
     */
    initialScans: IEquipment[];
    isLoading: boolean;
    loadError: boolean;
    retryLoad: () => void;
}

/**
 * Loads the kit that `productId` belongs to, then fetches all sibling
 * entries and resolves them to products. Only `EQUIPMENT`-typed products are
 * kept in the returned entries.
 *
 * A product is only ever part of a single kit, so the first lookup's
 * `kit_id` is treated as definitive.
 */
export const useKitForProduct = (productId: string | undefined, seed: IEquipment | null): UseKitForProductResult => {
    // Hold the seed in a ref so a re-render with a new equipment object (same
    // id) does not retrigger the loader effect.
    const seedRef = useRef<IEquipment | null>(seed);
    seedRef.current = seed;

    const [entries, setEntries] = useState<KitReturnEntry[]>([]);
    const [kitId, setKitId] = useState<string | null>(null);
    const [initialScans, setInitialScans] = useState<IEquipment[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(!!productId);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [reloadToken, setReloadToken] = useState<number>(0);

    useEffect(() => {
        if (!productId) {
            setKitId(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const run = async () => {
            setIsLoading(true);
            setLoadError(false);
            try {
                const ownEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), { product_id: productId });
                if (cancelled) return;

                const parentKitId = ownEntries[0]?.kit_id ?? null;
                if (!parentKitId) {
                    setKitId(null);
                    setEntries([]);
                    setInitialScans([]);
                    return;
                }
                setKitId(parentKitId);

                const kitEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), { kit_id: parentKitId });
                if (cancelled) return;

                if (kitEntries.length === 0) {
                    setKitId(parentKitId);
                    setEntries([]);
                    setInitialScans([]);
                    return;
                }

                const childIds = [...new Set(kitEntries.map((e) => e.product_id))];
                const resolved = await ProductClient.resolveProducts(childIds);
                if (cancelled) return;

                const productById = new Map(resolved.results.map((p) => [p.id, p]));
                const resolvedEntries: KitReturnEntry[] = [];
                for (const entry of kitEntries) {
                    const product = productById.get(entry.product_id);
                    if (!product) continue;
                    if (product.type !== ProductType.EQUIPMENT) continue;
                    resolvedEntries.push({ product, quantity: entry.quantity });
                }
                setEntries(resolvedEntries);

                const currentSeed = seedRef.current;
                if (currentSeed && resolvedEntries.some((e) => e.product.id === currentSeed.product_id)) {
                    setInitialScans([currentSeed]);
                } else {
                    setInitialScans([]);
                }
            } catch (e: unknown) {
                if (cancelled) return;
                setLoadError(true);
                ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT });
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [productId, reloadToken]);

    const retryLoad = useCallback(() => {
        setReloadToken((n) => n + 1);
    }, []);

    return { kitId, entries, initialScans, isLoading, loadError, retryLoad };
};
